import React from "react";

import { css } from "@emotion/css";
import { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { PractitionerBilling } from "@/models/PractitionerBillingModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type FileNameColumnProps = CellContext<PractitionerBilling, string>;

export const FileNameColumn: React.FC<FileNameColumnProps> = ({ renderValue, row }) => {
  const value = renderValue();
  const data = row.original;
  const styles = useStyles(makeStyles);
  const hovered = row.isHovered?.();

  return (
    <div className={styles.container}>
      <Typography variant="body">{value}</Typography>
      {hovered && (
        <Button
          data-action="account.billing.download"
          component="a"
          href={data.file.url}
          target="_blank"
          startIcon={<Icons.download className={styles.icon} />}
          variant="text"
        />
      )}
    </div>
  );
};

export const FileNameColumnHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <Typography variant="bodyLarge" className={styles.header}>
      {t("pages.practitionerBilling.fileName")}
    </Typography>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(4),
    padding: theme.spacing(0, 4),
  }),
  header: css({
    padding: theme.spacing(0, 4),
  }),
  icon: css({
    width: FontSizes.bodyLarge,
    height: FontSizes.bodyLarge,
  }),
});
